/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        User Workspace Authentication Screen (Login & Register) (Task 3.3 & Task 7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useMemo, useEffect, type FormEvent } from 'react';
import {
  Sparkles,
  Lock,
  Mail,
  User,
  Building2,
  ArrowRight,
  AlertCircle,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  Sliders,
} from 'lucide-react';
import { useAuthStore } from '../stores/useAuthStore';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import { useI18nStore } from '../stores/useI18nStore';
import { useTheme } from '../context/ThemeProvider';
import { getApiBaseUrl } from '../lib/api-client';
import { SocialLoginButtons } from '../components/auth/SocialLoginButtons';
import { FourElementsAuthLayout } from '../components/auth/FourElementsAuthLayout';
import { LOGO_VARIANTS } from '@sofia/assets';

export interface UserAuthViewProps {
  initialMode?: 'login' | 'register';
  onSuccess?: () => void;
  className?: string;
  variant?: 'default' | 'fourelements-ai';
}

export const UserAuthView: React.FC<UserAuthViewProps> = ({
  initialMode = 'login',
  onSuccess,
  className = '',
  variant,
}) => {
  const { t } = useI18nStore();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setSession = useAuthStore((state) => state.setSession);
  const { themeData, isDark } = useTheme();

  // Auto-redirect if session is active or becomes active
  useEffect(() => {
    if (isAuthenticated) {
      if (onSuccess) {
        onSuccess();
      } else if (typeof window !== 'undefined') {
        window.location.href = '/workspace/dashboard';
      }
    }
  }, [isAuthenticated, onSuccess]);

  const branding = themeData?.branding;
  const siteName = branding?.siteName || 'Sophia Platform';
  const slogan = branding?.slogan || 'Next-Gen Edge-Native Generative AI Studio';

  const fullLogoUrl =
    (isDark ? branding?.logoDarkUrl : branding?.logoLightUrl) ||
    (isDark ? LOGO_VARIANTS.fullDark : LOGO_VARIANTS.fullLight);
  const markLogoUrl = branding?.logoCollapsedUrl || LOGO_VARIANTS.mark;

  // Resolve layout variant: prop -> URL query param -> default
  const resolvedInitialVariant = useMemo<'default' | 'fourelements-ai'>(() => {
    if (variant) return variant;
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const queryLayout = params.get('auth_layout');
      if (queryLayout === 'fourelements-ai' || queryLayout === 'split') {
        return 'fourelements-ai';
      }
      if (queryLayout === 'default' || queryLayout === 'single') {
        return 'default';
      }
    }
    return 'default';
  }, [variant]);

  const [currentVariant, setCurrentVariant] = useState<'default' | 'fourelements-ai'>(resolvedInitialVariant);
  const [mode, setMode] = useState<'login' | 'register'>(initialMode);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [fullName, setFullName] = useState<string>('');
  const [workspaceName, setWorkspaceName] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Synchronize dynamic document title & favicon
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const actionTitle = mode === 'login' ? (t('auth.tabLogin') || 'Sign In') : (t('auth.tabRegister') || 'Sign Up');
    document.title = `${actionTitle} | ${siteName}`;

    const faviconHref = branding?.faviconUrl || '/favicon.ico';
    let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'shortcut icon';
      document.head.appendChild(link);
    }
    link.href = faviconHref;
  }, [mode, siteName, branding?.faviconUrl, t]);

  // Parse URL search params for error messages returned by OAuth callbacks
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    if (err) {
      setErrorMessage(err);
      // Clean query param
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsLoading(true);

    const baseUrl = getApiBaseUrl();
    const endpoint = mode === 'login' ? '/api/v1/auth/login' : '/api/v1/auth/register';

    try {
      const payload =
        mode === 'login'
          ? { email: email.trim(), password }
          : {
              email: email.trim(),
              password,
              fullName: fullName.trim() || email.split('@')[0],
              workspaceName: workspaceName.trim() || undefined,
            };

      const res = await fetch(`${baseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = (await res.json().catch(() => null)) as any;

      if (!res.ok || !data?.success) {
        // Extract server error message from standard error response, Zod issues array, or direct message
        let errorText = data?.error?.message;
        if (!errorText && Array.isArray(data?.error?.issues) && data.error.issues.length > 0) {
          errorText = data.error.issues
            .map((issue: any) => issue?.message)
            .filter(Boolean)
            .join('. ');
        }
        if (!errorText && typeof data?.message === 'string') {
          errorText = data.message;
        }

        // Accurate fallback based on HTTP status code if no error text is returned
        if (!errorText) {
          if (res.status === 409) {
            errorText = 'This email is already in use. Please sign in or use a different email.';
          } else if (res.status === 400) {
            errorText = 'Please verify that all fields meet the requirements.';
          } else if (res.status === 401) {
            errorText = 'Invalid email or password. Please check your credentials.';
          } else {
            errorText =
              mode === 'login'
                ? 'Sign in failed. Please check your credentials.'
                : 'Registration failed. Please try again.';
          }
        }

        throw new Error(errorText);
      }

      const { user, accessToken, workspace } = data.data;

      if (workspace) {
        useWorkspaceStore.getState().setWorkspaces([
          {
            id: workspace.id,
            name: workspace.name,
            slug: workspace.slug,
            role: 'OWNER',
            type: 'personal',
          },
        ]);
        useWorkspaceStore.getState().setActiveWorkspaceId(workspace.id);
      }

      setSession(accessToken, {
        id: user.id,
        email: user.email,
        name: user.fullName || user.email.split('@')[0],
        avatarUrl: user.avatarUrl || null,
        role: user.role || 'user',
      });

      setSuccessMessage(
        mode === 'login'
          ? t('auth.loginSuccess') || 'Signed in successfully!'
          : t('auth.registerSuccess') || 'Account created! Welcome to Sophia Platform.'
      );

      if (onSuccess) {
        onSuccess();
      } else if (typeof window !== 'undefined') {
        // Redirect to workspace dashboard
        window.location.href = '/workspace/dashboard';
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMessage(msg);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Reusable core authentication card containing brand logo, tab switcher, OAuth, and form.
   */
  const renderAuthCard = () => (
    <div className="w-full max-w-md space-y-6">
      {/* Brand Header */}
      <div className="text-center space-y-3">
        <div className="flex justify-center items-center">
          <img
            src={fullLogoUrl}
            alt={siteName}
            className="h-10 sm:h-12 w-auto max-w-[200px] object-contain transition-transform hover:scale-105"
            onError={(e) => {
              const target = e.currentTarget;
              if (target.src !== markLogoUrl && markLogoUrl) {
                target.src = markLogoUrl;
              } else {
                target.style.display = 'none';
                const fb = target.nextElementSibling as HTMLElement;
                if (fb) fb.style.display = 'inline-flex';
              }
            }}
          />
          <div
            style={{ display: 'none' }}
            className="items-center gap-2.5 px-3.5 py-2 rounded-2xl bg-primary/10 border border-primary/20 text-primary font-heading font-extrabold text-lg shadow-glow-primary"
          >
            <Sparkles className="size-5 text-primary" />
            <span>{siteName}</span>
          </div>
        </div>

        <h1 className="text-2xl font-heading font-bold text-foreground tracking-tight">
          {mode === 'login'
            ? (t('auth.loginTitle') ? `${t('auth.loginTitle')}` : `Welcome Back to ${siteName}`)
            : (t('auth.registerTitle') ? `${t('auth.registerTitle')}` : `Create Your Account on ${siteName}`)}
        </h1>
        <p className="text-xs text-foreground-muted max-w-sm mx-auto leading-relaxed">
          {mode === 'login'
            ? (slogan || t('auth.loginSubtitle') || 'Access your AI creation workspace, models, and studio workflows')
            : (t('auth.registerSubtitle') || 'Start with 5,000 free AI credits • No credit card required')}
        </p>
      </div>

      {/* Main Glassmorphism Auth Card */}
      <div className="p-6 sm:p-8 rounded-2xl border border-surface-border bg-surface shadow-glass backdrop-blur-xl space-y-6">
        {/* Mode Tab Switcher */}
        <div className="grid grid-cols-2 p-1 rounded-xl bg-muted/30 border border-surface-border text-xs font-semibold">
          <button
            type="button"
            onClick={() => {
              setMode('login');
              setErrorMessage(null);
            }}
            className={`py-2 rounded-lg transition-all ${
              mode === 'login'
                ? 'bg-surface text-foreground shadow-sm'
                : 'text-foreground-muted hover:text-foreground'
            }`}
          >
            {t('auth.tabLogin') || 'Sign In'}
          </button>
          <button
            type="button"
            onClick={() => {
              setMode('register');
              setErrorMessage(null);
            }}
            className={`py-2 rounded-lg transition-all ${
              mode === 'register'
                ? 'bg-surface text-foreground shadow-sm'
                : 'text-foreground-muted hover:text-foreground'
            }`}
          >
            {t('auth.tabRegister') || 'Sign Up'}
          </button>
        </div>

        {/* Social OAuth Logins (Google, GitHub, Apple) */}
        <div className="space-y-4">
          <SocialLoginButtons redirectPath="/workspace/dashboard" />

          <div className="relative flex items-center justify-center">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-surface-border" />
            </div>
            <span className="relative bg-surface px-3 text-[11px] uppercase tracking-wider text-foreground-muted font-medium">
              {t('auth.orContinueWith') || 'or continue with'}
            </span>
          </div>
        </div>

        {/* Error & Success Feedback Alerts */}
        {errorMessage && (
          <div className="p-3 rounded-xl border border-rose-500/20 bg-rose-500/10 text-rose-500 text-xs flex items-start gap-2.5 animate-fade-in">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{errorMessage}</span>
          </div>
        )}

        {successMessage && (
          <div className="p-3 rounded-xl border border-emerald-500/20 bg-emerald-500/10 text-emerald-500 text-xs flex items-center gap-2.5 animate-fade-in">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>{successMessage}</span>
          </div>
        )}

        {/* Email / Password Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground block">
                  {t('auth.fullNameLabel') || 'Full Name'}
                </label>
                <div className="relative">
                  <User className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-muted pointer-events-none" />
                  <input
                    type="text"
                    required
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder={t('auth.fullNamePlaceholder') || 'Alex Johnson'}
                    className="w-full pl-10 pr-4 py-2 rounded-xl text-xs sm:text-sm bg-muted/20 border border-surface-border text-foreground placeholder:text-foreground-muted/60 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground block">
                  {t('auth.workspaceNameLabel') || 'Workspace Name (Optional)'}
                </label>
                <div className="relative">
                  <Building2 className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-muted pointer-events-none" />
                  <input
                    type="text"
                    value={workspaceName}
                    onChange={(e) => setWorkspaceName(e.target.value)}
                    placeholder={t('auth.workspaceNamePlaceholder') || "Alex's Studio"}
                    className="w-full pl-10 pr-4 py-2 rounded-xl text-xs sm:text-sm bg-muted/20 border border-surface-border text-foreground placeholder:text-foreground-muted/60 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </div>
              </div>
            </>
          )}

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground block">
              {t('auth.emailLabel') || 'Email Address'}
            </label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-muted pointer-events-none" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder') || 'you@example.com'}
                className="w-full pl-10 pr-4 py-2 rounded-xl text-xs sm:text-sm bg-muted/20 border border-surface-border text-foreground placeholder:text-foreground-muted/60 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-foreground block">
                {t('auth.passwordLabel') || 'Password'}
              </label>
              {mode === 'login' && (
                <a
                  href="#forgot"
                  onClick={(e) => {
                    e.preventDefault();
                    setErrorMessage('Password reset is enabled through administrative email dispatch.');
                  }}
                  className="text-[11px] text-primary hover:underline"
                >
                  {t('auth.forgotPassword') || 'Forgot password?'}
                </a>
              )}
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-muted pointer-events-none" />
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full pl-10 pr-10 py-2 rounded-xl text-xs sm:text-sm bg-muted/20 border border-surface-border text-foreground placeholder:text-foreground-muted/60 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-mono"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground transition-colors p-1"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {mode === 'register' && (
              <p className="text-[11px] text-foreground-muted/80 leading-normal">
                {t('auth.passwordHint') || 'Must be at least 8 characters with uppercase, lowercase, number, and special character.'}
              </p>
            )}
          </div>

          {mode === 'login' && (
            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="remember-me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="rounded border-surface-border text-primary focus:ring-primary/20 w-3.5 h-3.5"
              />
              <label htmlFor="remember-me" className="text-xs text-foreground-muted cursor-pointer select-none">
                {t('auth.rememberMe') || 'Keep me logged in'}
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-primary text-primary-fg font-semibold text-xs sm:text-sm shadow-glow-primary hover:bg-primary-hover transition-all active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed mt-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>
                  {mode === 'login'
                    ? t('auth.signingIn') || 'Signing in...'
                    : t('auth.signingUp') || 'Creating account...'}
                </span>
              </>
            ) : (
              <>
                <span>
                  {mode === 'login'
                    ? t('auth.btnSignIn') || 'Sign In with Email'
                    : t('auth.btnSignUp') || 'Create Free Account'}
                </span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        {/* Legal / Terms Notice */}
        <p className="text-[11px] text-foreground-muted text-center leading-relaxed">
          {t('auth.termsNotice') ||
            'By continuing, you agree to our Terms of Service and Privacy Policy.'}
        </p>
      </div>

      {/* Footer Switch Prompt & Layout Switcher */}
      <div className="space-y-3 text-center">
        <div className="text-xs text-foreground-muted">
          {mode === 'login' ? (
            <>
              <span>{t('auth.noAccount') || "Don't have an account?"} </span>
              <button
                type="button"
                onClick={() => {
                  setMode('register');
                  setErrorMessage(null);
                }}
                className="text-primary font-semibold hover:underline"
              >
                {t('auth.tabRegister') || 'Sign Up'}
              </button>
            </>
          ) : (
            <>
              <span>{t('auth.hasAccount') || 'Already have an account?'} </span>
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  setErrorMessage(null);
                }}
                className="text-primary font-semibold hover:underline"
              >
                {t('auth.tabLogin') || 'Sign In'}
              </button>
            </>
          )}
        </div>

        {/* Layout Style Toggle Pill */}
        <div className="flex justify-center items-center">
          <button
            type="button"
            onClick={() =>
              setCurrentVariant(currentVariant === 'default' ? 'fourelements-ai' : 'default')
            }
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-surface-border bg-surface/80 hover:bg-surface text-[11px] font-medium text-foreground-muted hover:text-foreground transition-all shadow-xs"
            title="Toggle between Centered Card and Split Showcase Layout"
          >
            <Sliders className="w-3 h-3 text-primary" />
            <span>
              {currentVariant === 'default'
                ? t('auth.switchLayoutSplit')
                : t('auth.switchLayoutDefault')}
            </span>
          </button>
        </div>
      </div>
    </div>
  );

  // Variant A: Split-Screen Four Elements AI Template
  if (currentVariant === 'fourelements-ai') {
    return (
      <FourElementsAuthLayout
        mode={mode}
        siteName={siteName}
        slogan={slogan}
      >
        {renderAuthCard()}
      </FourElementsAuthLayout>
    );
  }

  // Variant B: Centered Single Card Layout (Default)
  return (
    <div
      className={`min-h-screen w-full flex items-center justify-center p-4 bg-background-base text-foreground select-none ${className}`}
    >
      {renderAuthCard()}
    </div>
  );
};
